// oxlint-disable-next-line import-x-js/no-extraneous-dependencies
import { layout as dagreLayout, graphlib } from 'dagre';
import { ComponentType, useMemo } from 'react';
// oxlint-disable-next-line import-x-js/no-extraneous-dependencies
import {
  Background,
  Controls,
  Edge,
  Handle,
  MarkerType,
  Node,
  NodeProps,
  Position,
  ReactFlow,
  ReactFlowProvider,
} from 'reactflow';
// oxlint-disable-next-line import-x-js/no-extraneous-dependencies
import 'reactflow/dist/style.css';

import { QBox, QChip, QLink, QStack, QTypography } from '@captivateiq/quotient';

import type { ColumnTraceBlock, ColumnTraceNode as ColumnTraceNodeType } from '@captivateiq/assistant/types';

type Props = { block: ColumnTraceBlock };

const COLUMN_NODE_WIDTH = 320;
const COLUMN_NODE_HEIGHT = 120;
const TRANSFORMATION_NODE_WIDTH = 200;
const TRANSFORMATION_NODE_HEIGHT = 30;
const NODE_SEP = 60;
const RANK_SEP = 80;
const CONTAINER_HEIGHT = 420;

const EDGE_STYLE = {
  style: { strokeWidth: '2px', stroke: '#000000' },
  markerEnd: { type: MarkerType.Arrow, color: '#000000' },
};

const HANDLE_STYLE = { visibility: 'hidden' as const, width: 0, height: 0 };

type ColumnNodeData = {
  nodeType: string;
  name: string;
  worksheetName: string;
  variant: string;
  dataType: string;
  formula?: string | null;
  value?: string | null;
  isRoot?: boolean;
  tags?: string[];
  links?: Record<string, string>;
};

const getVariantChipColor = (
  variant: string,
): 'primary' | 'secondary' | 'tertiary' | 'quaternary' | 'neutralPrimary' => {
  switch (variant) {
    case 'derived':
    case 'payout':
      return 'quaternary';
    case 'raw':
      return 'primary';
    case 'transformed':
      return 'tertiary';
    default:
      return 'neutralPrimary';
  }
};

const ColumnNodeComponent = ({ data }: NodeProps<ColumnNodeData>) => {
  const variantChipColor = getVariantChipColor(data.variant);
  const linkEntries = data.links ? Object.entries(data.links) : [];

  return (
    <QStack
      sx={{
        width: COLUMN_NODE_WIDTH,
        bgcolor: 'surface.background.highContrast',
        border: data.isRoot ? '2px solid' : '1px solid',
        borderColor: data.isRoot ? 'primary.main' : 'border.neutralSecondary.mediumContrast',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Handle isConnectable={false} position={Position.Top} style={HANDLE_STYLE} type="target" />
      <QStack spacing="4px" sx={{ p: 1.5 }}>
        <QStack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <QChip
            color="neutralPrimary"
            label={data.worksheetName}
            size="small"
            sx={{ maxWidth: COLUMN_NODE_WIDTH / 2.5 }}
          />
          <QChip color={variantChipColor} label={data.variant} size="small" />
          {data.isRoot && <QChip color="primary" label="root" size="small" />}
        </QStack>
        <QTypography color="text.onSurface.background.highContrast" variant="smallHeading">
          {data.name}
        </QTypography>
        {data.value && (
          <QTypography color="text.onSurface.background.highContrast" variant="smallRegular">
            {data.value}
          </QTypography>
        )}
        {data.formula && (
          <QBox
            sx={{
              p: 1,
              bgcolor: '#FBFBFB',
              border: '1px solid',
              borderColor: 'border.neutralPrimary.mediumContrast',
              borderRadius: 1,
              maxHeight: 40,
              overflow: 'hidden',
            }}
          >
            <QTypography
              sx={{
                fontFamily: 'monospace',
                fontSize: '12px',
                wordBreak: 'break-all',
                lineHeight: 1.3,
              }}
            >
              {data.formula}
            </QTypography>
          </QBox>
        )}
        {linkEntries.length > 0 && (
          <QStack direction="row" spacing={1}>
            {linkEntries.map(([label, url]) => (
              <QLink href={url} key={label} rel="noopener noreferrer" sx={{ fontSize: '11px' }} target="_blank">
                {label}
              </QLink>
            ))}
          </QStack>
        )}
      </QStack>
      <Handle isConnectable={false} position={Position.Bottom} style={HANDLE_STYLE} type="source" />
    </QStack>
  );
};

const TransformationNodeComponent = ({ data }: NodeProps<ColumnNodeData>) => (
  <QStack
    alignItems="center"
    justifyContent="center"
    sx={{
      width: TRANSFORMATION_NODE_WIDTH,
      height: TRANSFORMATION_NODE_HEIGHT,
      bgcolor: 'primary.200',
      border: '1px solid lightGray',
      borderRadius: 1,
    }}
  >
    <Handle isConnectable={false} position={Position.Top} style={HANDLE_STYLE} type="target" />
    <QTypography color="primary.700" variant="smallRegular">
      {data.name}
    </QTypography>
    <Handle isConnectable={false} position={Position.Bottom} style={HANDLE_STYLE} type="source" />
  </QStack>
);

const nodeTypes: Record<string, ComponentType<NodeProps>> = {
  columnTrace: ColumnNodeComponent,
  transformation: TransformationNodeComponent,
};

const buildDagreLayout = (
  traceNodes: ColumnTraceNodeType[],
  traceEdges: ColumnTraceBlock['edges'],
  rootColumnId: string,
): { nodes: Node[]; edges: Edge[]; rootPosition: { x: number; y: number } | null } => {
  if (traceNodes.length === 0) {
    return { nodes: [], edges: [], rootPosition: null };
  }

  const dagreGraph = new graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: 'TB', nodesep: NODE_SEP, ranksep: RANK_SEP });

  traceNodes.forEach((node) => {
    const isTransformation = node.type === 'transformation';
    const width = isTransformation ? TRANSFORMATION_NODE_WIDTH : COLUMN_NODE_WIDTH;
    const height = isTransformation ? TRANSFORMATION_NODE_HEIGHT : COLUMN_NODE_HEIGHT;
    dagreGraph.setNode(node.id, { width, height });
  });

  traceEdges.forEach((edge: ColumnTraceBlock['edges'][number]) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagreLayout(dagreGraph);

  let rootPosition: { x: number; y: number } | null = null;

  const nodes: Node[] = traceNodes.map((node) => {
    const pos = dagreGraph.node(node.id);
    const isTransformation = node.type === 'transformation';
    const width = isTransformation ? TRANSFORMATION_NODE_WIDTH : COLUMN_NODE_WIDTH;
    const height = isTransformation ? TRANSFORMATION_NODE_HEIGHT : COLUMN_NODE_HEIGHT;
    const x = pos.x - width / 2;
    const y = pos.y - height / 2;

    if (node.id === rootColumnId) {
      rootPosition = { x: pos.x, y: pos.y };
    }

    return {
      id: node.id,
      type: isTransformation ? 'transformation' : 'columnTrace',
      position: { x, y },
      data: {
        nodeType: node.type ?? 'column',
        name: node.name,
        worksheetName: node.worksheetName,
        variant: node.variant,
        dataType: node.dataType,
        formula: node.formula,
        value: node.value,
        isRoot: node.isRoot,
        tags: node.tags,
        links: node.links,
      },
    };
  });

  const edges: Edge[] = traceEdges.map((edge: ColumnTraceBlock['edges'][number]) => ({
    ...EDGE_STYLE,
    id: `${edge.source}->${edge.target}`,
    source: edge.source,
    target: edge.target,
    animated: edge.isCrossSchema,
  }));

  return { nodes, edges, rootPosition };
};

export const ColumnTraceBlockRenderer = ({ block }: Props) => {
  const { nodes, edges, rootPosition } = useMemo(
    () => buildDagreLayout(block.nodes, block.edges, block.rootColumnId),
    [block.nodes, block.edges, block.rootColumnId],
  );

  const defaultViewport = useMemo(() => {
    if (!rootPosition) {
      return { x: 0, y: 0, zoom: 1 };
    }
    return {
      x: -(rootPosition as { x: number; y: number }).x + COLUMN_NODE_WIDTH / 2 + 40,
      y: -(rootPosition as { x: number; y: number }).y + CONTAINER_HEIGHT / 2,
      zoom: 1,
    };
  }, [rootPosition]);

  if (nodes.length === 0) {
    return null;
  }

  return (
    <QBox
      sx={{
        bgcolor: 'surface.background.highContrast',
        border: '1px solid',
        borderColor: 'border.neutralSecondary.mediumContrast',
        borderRadius: 1,
        mb: 2,
        overflow: 'hidden',
      }}
    >
      <QBox sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
        <QTypography color="text.onSurface.background.highContrast" variant="smallHeading">
          {block.title}
        </QTypography>
      </QBox>
      <QBox sx={{ height: CONTAINER_HEIGHT, width: '100%' }}>
        <ReactFlowProvider>
          <ReactFlow
            defaultViewport={defaultViewport}
            edges={edges}
            maxZoom={2}
            minZoom={0.3}
            nodes={nodes}
            nodeTypes={nodeTypes}
            panOnScroll
            proOptions={{ hideAttribution: true }}
          >
            <Controls showInteractive={false} />
            <Background gap={16} size={1} />
          </ReactFlow>
        </ReactFlowProvider>
      </QBox>
    </QBox>
  );
};
